// Layout utilities for PDF generation
import { PDF_CONFIG, getContentWidth } from '../config.js';

/**
 * Check if we need a page break and add one if necessary
 * @param {jsPDF} pdf - The PDF document
 * @param {number} currentY - Current Y position
 * @param {number} requiredSpace - Space needed for next content
 * @returns {number} - New Y position (reset if page break occurred)
 */
export const checkPageBreak = (pdf, currentY, requiredSpace = 20) => {
  const maxY = PDF_CONFIG.pageHeight - PDF_CONFIG.margins.bottom - 15;

  if (currentY + requiredSpace > maxY) {
    pdf.addPage();
    return PDF_CONFIG.margins.top + PDF_CONFIG.headerHeight + 5;
  }

  return currentY;
};

/**
 * Render a section title with consistent styling
 * @param {jsPDF} pdf - The PDF document
 * @param {string} title - Section title text
 * @param {number} y - Y position
 * @returns {number} - New Y position after title
 */
export const renderSectionTitle = (pdf, title, y) => {
  const { margins, colors, fonts } = PDF_CONFIG;

  // Check for page break
  y = checkPageBreak(pdf, y, 20);

  // Draw accent line
  pdf.setDrawColor(...colors.navyHeader);
  pdf.setLineWidth(0.5);
  pdf.line(margins.left, y, margins.left + 40, y);

  y += 6;

  // Title text
  pdf.setFontSize(fonts.sectionHeader);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...colors.navyHeader);
  pdf.text(title, margins.left, y);

  return y + 8;
};

/**
 * Render a subsection title
 * @param {jsPDF} pdf - The PDF document
 * @param {string} title - Subsection title text
 * @param {number} y - Y position
 * @returns {number} - New Y position after title
 */
export const renderSubsectionTitle = (pdf, title, y) => {
  const { margins, colors, fonts } = PDF_CONFIG;

  y = checkPageBreak(pdf, y, 15);

  pdf.setFontSize(fonts.subHeader);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...colors.darkText);
  pdf.text(title, margins.left, y);

  return y + 6;
};

/**
 * Add page header to a specific page (called after all content is added)
 * @param {jsPDF} pdf - The PDF document
 * @param {number} pageNum - Page number
 * @param {object} options - Options including reportId, userEmail
 */
export const addPageHeader = (pdf, pageNum, options = {}) => {
  const { pageWidth, margins, colors, headerHeight } = PDF_CONFIG;

  pdf.setPage(pageNum);

  // Navy header bar
  pdf.setFillColor(...colors.navyHeader);
  pdf.rect(0, 0, pageWidth, headerHeight, 'F');

  // Brand name
  pdf.setTextColor(...colors.white);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('RebalanceKit', margins.left, 12);

  // Tagline
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Portfolio Rebalancing Report', margins.left, 20);

  // Right side metadata
  const rightX = pageWidth - margins.right;
  pdf.setFontSize(8);

  if (options.reportId) {
    pdf.text(`Report: ${options.reportId}`, rightX, 12, { align: 'right' });
  }

  if (options.generatedDate) {
    pdf.text(options.generatedDate, rightX, 20, { align: 'right' });
  }

  if (options.userEmail && pageNum === 1) {
    pdf.text(`Prepared for: ${options.userEmail}`, rightX, 28, { align: 'right' });
  }
};

/**
 * Add footer to all pages
 * @param {jsPDF} pdf - The PDF document
 */
export const addFooters = (pdf) => {
  const totalPages = pdf.internal.getNumberOfPages();
  const { pageWidth, pageHeight, margins, colors, fonts } = PDF_CONFIG;

  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);

    // Footer line
    pdf.setDrawColor(...colors.mediumGray);
    pdf.setLineWidth(0.3);
    pdf.line(margins.left, pageHeight - 18, pageWidth - margins.right, pageHeight - 18);

    // Page number (center)
    pdf.setFontSize(fonts.footer);
    pdf.setTextColor(...colors.grayText);
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 12, { align: 'center' });

    // Branding (left)
    pdf.text('Generated by RebalanceKit', margins.left, pageHeight - 12);

    // Confidentiality (right)
    pdf.setFontSize(6);
    pdf.text('CONFIDENTIAL', pageWidth - margins.right, pageHeight - 12, { align: 'right' });
  }
};

/**
 * Generate a unique report ID
 * @returns {string} - Report ID in format RK-YYYYMMDD-XXXX
 */
export const generateReportId = () => {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `RK-${dateStr}-${random}`;
};

/**
 * Format date for display
 * @param {Date} date - Date object
 * @returns {string} - Formatted date string
 */
export const formatReportDate = (date = new Date()) => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Wrap text to fit within content width
 * @param {jsPDF} pdf - The PDF document
 * @param {string} text - Text to wrap
 * @param {number} maxWidth - Maximum width (default: content width)
 * @returns {string[]} - Array of lines
 */
export const wrapText = (pdf, text, maxWidth = null) => {
  const width = maxWidth || getContentWidth();
  return pdf.splitTextToSize(text, width);
};

/**
 * Draw a rounded rectangle box
 * @param {jsPDF} pdf - The PDF document
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {number} width - Box width
 * @param {number} height - Box height
 * @param {number[]} fillColor - RGB color array
 * @param {number[]} borderColor - RGB color array (optional)
 */
export const drawBox = (pdf, x, y, width, height, fillColor, borderColor = null) => {
  pdf.setFillColor(...fillColor);

  if (borderColor) {
    pdf.setDrawColor(...borderColor);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(x, y, width, height, 2, 2, 'FD');
  } else {
    pdf.roundedRect(x, y, width, height, 2, 2, 'F');
  }
};
